import React, { useRef, useEffect } from "react";
import { Divider } from "@mui/material";
import { useFormData } from "./useFormData";
import {
    Text, Form, FlexContainer, LottieLoadingIndicator, ImageCropModal,
    PortalFormActions, PortalFormSection, PortalFormSectionDescription,
    PortalFormSectionHeader, PortalFormSectionTitle, PortalFormScrollBody,
    PortalFormSurface, StandardModalActionButton, StandardModalActionGroup,
    portalColors
} from "src/components";
import { useImageUpload } from "../hooks/useImageUpload";
import { getErrorMessage } from "../constants/validationErrors";
import { useFormValidation, useImageRetrieveUrl, useSaveQuoteAndArticle } from "../hooks";
import { BackgroundImage, QuoteTextField, CustomizeTextField, 
    AdjustTextSizeField, TextColorField, 
    TextAlignmentField, AddArticleImageField, 
    ArticleTitleField, ArticleDescriptionField, 
    ArticleWebLinkField 
} from "./fields";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";
import MobilePreview from "./MobilePreview";

const FieldForm = ({ params, manageQuotes, quote, settingsPreview }) => {
    const quoteImageRef = useRef();
    const articleImageRef = useRef();
    const isMounted = useRef(true);
    const { formData, updateField, updateNestedField, formErrors, setFormErrors } = useFormData();
    const { 
        formSubmitLoading, 
        setFormSubmitLoading,
        setQuoteImage,
        quoteImage, 
        setArticleImage,
        articleImage, 
        handleAddQuoteImage, 
        handleAddArticleImage, 
        cropImages
    } = useImageUpload(updateNestedField, getErrorMessage, setFormErrors, formErrors);
    const quoteFile = cropImages.quoteCroppedBlob;
    const articleFile = cropImages.articleCroppedBlob;
    const { validateForm } = useFormValidation();
    const { uploadQuoteImage, uploadArticleImage } = useImageRetrieveUrl();
    const { saveData, updateData } = useSaveQuoteAndArticle();

    const quoteUuid = quote?.uuid || "";
    const renderQuoteImage = quote?.imageUrl || "";
    const renderArticleImage = quote?.article?.imageUrl || "";
    const bgImageQuote = { quoteImageRef, quoteImage, handleAddQuoteImage, renderQuoteImage, quoteUuid };
    const bgImageArticle = { articleImageRef, articleImage, handleAddArticleImage, renderArticleImage, quoteUuid };
    
    // Set companyUuid on mount
    useEffect(() => {
        if (params.companyUuid) {
            updateField("companyUuid", params.companyUuid);
        }
    }, [params.companyUuid]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleSubmitQuotes = async () => {
        setFormSubmitLoading(true);

        const errors = validateForm({ quoteImage, articleImage, formData, isUpdating: !!formData.uuid });
        if (errors.length > 0) {
            setFormErrors(errors);
            setFormSubmitLoading(false);
            return;
        }

        try {
            let updatedQuoteData = {};
            let updatedArcImage = null;

            if (quoteFile) updatedQuoteData = await uploadQuoteImage(quoteFile, quoteUuid);
            let safeUuid = updatedQuoteData.uuid || quoteUuid;
            if (articleFile) updatedArcImage = await uploadArticleImage(articleFile, safeUuid);

            const payload = {
                ...formData,
                ...(updatedQuoteData && {
                    imageUrl: updatedQuoteData.imageUrl ?? quote?.imageUrl,
                    thumbnailUrl: updatedQuoteData.thumbnailUrl ?? quote?.thumbnailUrl,
                    uuid: updatedQuoteData.uuid ?? quote?.uuid,
                }),
                article: {
                    ...formData.article,
                    ...(updatedArcImage && { imageUrl: updatedArcImage ?? quote?.article?.imageUrl }),
                },
            };

            if (quoteUuid) {
                await updateData(params, manageQuotes, quoteUuid, payload);
            } else {
                await saveData(params, manageQuotes, payload);
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            if (isMounted.current) {
                setFormSubmitLoading(false);
            }
        }
    };

    return (
        <PortalFormSurface maxWidth="94.5rem" marginTop="0" flex="1 1 auto" minHeight="0">
            <Form paddingBottom="0" height="100%" width="100%" onSubmit={(e) => { e.preventDefault(); }} style={{ margin: 0, display: "flex", flexDirection: "column", minHeight: 0 }} >
                <PortalFormScrollBody columns="minmax(0, 1fr) minmax(20rem, 0.7fr)" padding="1.25rem" gap="1rem">
                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Quote Content
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Upload the artwork, write the quote and attach an optional article.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>
                        <FlexContainer minWidth="0" style={{ flexShrink: 0, gap: "1rem" }} display="flex" align="left">
                            <BackgroundImage
                                error={getErrorMessage(formErrors, "imageUrl")}
                                value={bgImageQuote}
                            />

                            <QuoteTextField
                                value={formData.quoteText}
                                onChange={(val) => updateField("quoteText", val)}
                                isUpdate={quoteUuid}
                            />

                            <CustomizeTextField
                                value={formData.customization.textFont}
                                onChange={(val) => updateNestedField("customization", "textFont", val)}
                            />

                            <TextColorField
                                value={formData.customization.textColor}
                                isUpdate={quoteUuid}
                                onChange={(val) => updateNestedField("customization", "textColor", val)}
                            />

                            <AdjustTextSizeField
                                value={formData.customization.fontSize}
                                onChange={(val) => updateNestedField("customization", "fontSize", val)}
                            />

                            <TextAlignmentField
                                value={{
                                    textAlign: formData.customization.textAlign,
                                    verticalAlign: formData.customization.verticalAlign
                                }}
                                onChange={(val) => {
                                    updateNestedField("customization", "textAlign", val.textAlign);
                                    updateNestedField("customization", "verticalAlign", val.verticalAlign);
                                }}
                            />

                            <Divider style={{ width: "100%", height: "2px", marginBottom: "25px" }}/>

                            <AddArticleImageField
                                error={getErrorMessage(formErrors, "articleImageUrl")}
                                value={bgImageArticle}
                            />

                            <ArticleTitleField
                                value={formData.article.title}
                                onChange={(val) => updateNestedField("article", "title", val)}
                                error={getErrorMessage(formErrors, "articleTitle")}
                            />

                            <ArticleDescriptionField
                                htmlContent={formData.article.articleHtml}
                                onChange={(val) => updateNestedField("article", "articleHtml", val)}
                                error={getErrorMessage(formErrors, "articleBody")}
                            />

                            <ArticleWebLinkField
                                value={formData.article.referenceLinks}
                                onChange={(val) => updateNestedField("article", "referenceLinks", val)}
                            />

                            <Divider style={{ width: "100%", height: "2px" }}/>

                            <FlexContainer>
                                <Text align="left" color={portalColors.mutedText}>
                                    You can always edit or delete your custom quotes later from your Business Portal.
                                </Text>
                            </FlexContainer>
                        </FlexContainer>
                    </PortalFormSection>
                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Quote Preview
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Review how the quote will appear in the app.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>
                        <MobilePreview settingsPreview={settingsPreview}/>
                    </PortalFormSection>
                </PortalFormScrollBody>
                <PortalFormActions sticky={false}>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" disabled={formSubmitLoading} onClick={(e) => manageQuotes.createQuotes(e)}>
                            Cancel
                        </StandardModalActionButton>
                        <StandardModalActionButton type="button" $variant="primary" disabled={formSubmitLoading} onClick={() => handleSubmitQuotes()}>
                            {formSubmitLoading
                                ? <LottieLoadingIndicator height="1.5rem" width="1.5rem" containerHeight="1.5rem" margin="0" />
                                : "Publish Quote"}
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </PortalFormActions>
            </Form>
            <ImageCropModal
                imageSrc={cropImages.imageToCrop?.src}
                isOpen={cropImages.showCropperModal}
                onClose={(shouldClear) => {
                    cropImages.setShowCropperModal(false);
                    if (shouldClear) {
                        const { type } = cropImages.imageToCrop || {};
                        if (type === "quote") {
                            const fallback = quote?.imageUrl || "";
                            setQuoteImage(fallback);
                            updateNestedField("objectImage", "quotes", fallback);
                        } else if (type === "article") {
                            const fallback = quote?.article?.imageUrl || "";
                            setArticleImage(fallback);
                            updateNestedField("objectImage", "article", fallback);
                        }
                        cropImages.setImageToCrop(null);
                    }
                }}
                onCropComplete={(file) => cropImages.handleCropComplete(file, cropImages.imageToCrop.type)}
                aspectRatios={
                    cropImages.imageToCrop?.type === "quote"
                        ? [{ width: 9, height: 16, label: "9:16", standardName: ASPECT_RATIO_LABEL.PORTRAIT }]
                        : [{ width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN }]
                }
                allowedOrientations={
                    cropImages.imageToCrop?.type === "quote"
                        ? ["portrait"]
                        : ["landscape"]
                }
                lockedAspectRatio={undefined}
            />
        </PortalFormSurface>
    );
};

export default FieldForm;
